import { useMemo, useState } from 'react';
import { ARTIFACT_TYPE_LABELS, artifactDataSchemas, type ArtifactType } from '@thottbot/shared';
import {
  FORM_FIELDS,
  dataToValues,
  valuesToData,
  type FieldDef,
  type FormValues,
} from './formFields';
import { useZoneNames } from './useZoneNames';

export interface ArtifactFormSubmit {
  name: string;
  data: Record<string, unknown>;
  note: string;
  verifiedInForever: boolean;
}

interface Props {
  type: ArtifactType;
  /** Existing artifact for edits; omit for a new submission. */
  initial?: { name: string; data: Record<string, unknown> };
  mode: 'create' | 'edit';
  busy?: boolean;
  serverError?: string | null;
  onSubmit: (value: ArtifactFormSubmit) => void;
}

export function ArtifactForm({ type, initial, mode, busy, serverError, onSubmit }: Props) {
  const fields = FORM_FIELDS[type];
  const [name, setName] = useState(initial?.name ?? '');
  const [values, setValues] = useState<FormValues>(() => dataToValues(type, initial?.data ?? {}));
  const [note, setNote] = useState('');
  const [verified, setVerified] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const zones = useZoneNames();
  const label = ARTIFACT_TYPE_LABELS[type].singular;

  const set = (key: string, value: string) => setValues((v) => ({ ...v, [key]: value }));

  const preview = useMemo(
    () => valuesToData(type, values, initial?.data ?? {}),
    [type, values, initial],
  );

  const submit = (e: { preventDefault(): void }) => {
    e.preventDefault();
    const problems: string[] = [];
    if (name.trim().length < 2) problems.push('Name must be at least 2 characters.');
    const parsed = artifactDataSchemas[type].safeParse(preview);
    if (!parsed.success) {
      for (const issue of parsed.error.issues)
        problems.push(`${issue.path.join('.') || 'data'}: ${issue.message}`);
    }
    setErrors(problems);
    if (problems.length) return;
    onSubmit({ name: name.trim(), data: preview, note: note.trim(), verifiedInForever: verified });
  };

  return (
    <form
      onSubmit={submit}
      noValidate
      aria-label={`${mode === 'create' ? 'Submit' : 'Edit'} ${label}`}
    >
      <div className="form-row">
        <label htmlFor="f-name">Name</label>
        <input
          id="f-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={200}
        />
      </div>
      {fields.map((f) => (
        <Field key={f.path} def={f} values={values} set={set} zones={zones} />
      ))}
      <div className="form-row">
        <label htmlFor="f-note">Note for moderators</label>
        <input
          id="f-note"
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={500}
          placeholder="Where did this come from?"
        />
      </div>
      <div className="form-row">
        <label htmlFor="f-verified">Verified in WoW: Forever</label>
        <span>
          <input
            id="f-verified"
            type="checkbox"
            checked={verified}
            onChange={(e) => setVerified(e.target.checked)}
          />{' '}
          I checked this in the Forever client
        </span>
      </div>
      {(errors.length > 0 || serverError) && (
        <div className="notice error" role="alert">
          {serverError && <div>{serverError}</div>}
          {errors.map((er) => (
            <div key={er}>{er}</div>
          ))}
        </div>
      )}
      <div className="actions">
        <button type="submit" disabled={busy}>
          {mode === 'create' ? 'Submit for review' : 'Submit edit for review'}
        </button>
      </div>
    </form>
  );
}

function Field({
  def,
  values,
  set,
  zones,
}: {
  def: FieldDef;
  values: FormValues;
  set: (k: string, v: string) => void;
  zones: string[];
}) {
  const id = `f-${def.path.replace(/\./g, '-')}`;
  const v = values[def.path] ?? '';
  let control;
  switch (def.kind) {
    case 'textarea':
    case 'refs':
    case 'stats':
    case 'reagents':
      control = (
        <textarea
          id={id}
          value={v}
          onChange={(e) => set(def.path, e.target.value)}
          rows={def.kind === 'textarea' ? 4 : 3}
        />
      );
      break;
    case 'number':
      control = (
        <input
          id={id}
          type="number"
          value={v}
          min={def.min}
          max={def.max}
          onChange={(e) => set(def.path, e.target.value)}
        />
      );
      break;
    case 'select':
      control = (
        <select id={id} value={v} onChange={(e) => set(def.path, e.target.value)}>
          <option value="">(not set)</option>
          {def.options?.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
      break;
    case 'zone':
      control = (
        <>
          <input
            id={id}
            type="text"
            list="zone-list"
            value={v}
            onChange={(e) => set(def.path, e.target.value)}
            maxLength={200}
          />
          <datalist id="zone-list">
            {zones.map((z) => (
              <option key={z} value={z} />
            ))}
          </datalist>
        </>
      );
      break;
    case 'damage':
      control = (
        <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <input
            aria-label={`${def.label} min`}
            type="number"
            min={0}
            placeholder="min"
            value={values[`${def.path}.min`] ?? ''}
            onChange={(e) => set(`${def.path}.min`, e.target.value)}
            style={{ width: 70 }}
          />
          <input
            aria-label={`${def.label} max`}
            type="number"
            min={0}
            placeholder="max"
            value={values[`${def.path}.max`] ?? ''}
            onChange={(e) => set(`${def.path}.max`, e.target.value)}
            style={{ width: 70 }}
          />
          <input
            aria-label={`${def.label} speed`}
            type="number"
            min={0}
            step="0.1"
            placeholder="speed"
            value={values[`${def.path}.speed`] ?? ''}
            onChange={(e) => set(`${def.path}.speed`, e.target.value)}
            style={{ width: 70 }}
          />
        </span>
      );
      break;
    default:
      control = (
        <input
          id={id}
          type="text"
          value={v}
          onChange={(e) => set(def.path, e.target.value)}
          maxLength={200}
        />
      );
  }
  return (
    <div className="form-row">
      <label htmlFor={def.kind === 'damage' ? undefined : id}>{def.label}</label>
      {control}
      {def.help && (
        <span className="muted" style={{ flexBasis: '100%' }}>
          {def.help}
        </span>
      )}
    </div>
  );
}
